import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import PageTitle from "./PageTitle";
import { titleForPath } from "@/lib/pageTitles";
import { useState } from "react";
import {
  LayoutDashboard,
  Building2,
  Users,
  Shield,
  LogOut,
  Star,
  ChevronLeft,
  Tag,
  Key,
  FileText,
  Inbox,
  Menu,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";

const NAV_ITEMS = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/agencies", label: "Agencies", icon: Building2 },
  { to: "/admin/users", label: "All Users", icon: Users },
  { to: "/admin/promos", label: "Promo Codes", icon: Tag },
  { to: "/admin/api-keys", label: "API Keys", icon: Key },
  { to: "/admin/inquiries", label: "Inquiries", icon: Inbox },
  { to: "/admin/blog", label: "Blog", icon: FileText },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const adminScopes = Array.isArray(user?.admin_scopes) ? user.admin_scopes : [];
  const navItems = user?.is_superadmin ? NAV_ITEMS : NAV_ITEMS.filter((item) => item.to === "/admin/blog");

  const initials = (user?.full_name || "SA")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0F0F12] text-white">
      <PageTitle title={titleForPath(pathname)} admin />
      <aside className="fixed left-0 top-0 hidden h-screen w-60 border-r border-white/10 bg-[#18181B] lg:flex lg:flex-col">
        <AdminSidebar navItems={navItems} user={user} initials={initials} adminScopes={adminScopes} onLogout={handleLogout} />
      </aside>

      <div className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-white/10 bg-[#0F0F12]/95 px-4 py-3 backdrop-blur lg:hidden">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white text-[#0F0F12]">
            <Star className="w-4 h-4 fill-[#0F0F12]" strokeWidth={0} />
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold tracking-tight">Polaris Admin</div>
            <div className="truncate text-caption uppercase tracking-widest text-white/40">Control Panel</div>
          </div>
        </div>
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger asChild>
            <button type="button" className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5" aria-label="Open admin navigation">
              <Menu className="w-5 h-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[88vw] max-w-sm overflow-y-auto border-r border-white/10 bg-[#18181B] p-0 text-white">
            <SheetHeader className="border-b border-white/10 px-4 py-4">
              <div className="flex items-center gap-2.5 pr-10">
                <div className="grid h-8 w-8 place-items-center rounded-lg bg-white text-[#0F0F12]">
                  <Star className="w-4 h-4 fill-[#0F0F12]" strokeWidth={0} />
                </div>
                <div>
                  <SheetTitle className="text-left text-sm font-semibold tracking-tight text-white">Polaris Admin</SheetTitle>
                  <div className="text-caption uppercase tracking-widest text-white/40">Control Panel</div>
                </div>
              </div>
            </SheetHeader>
            <AdminSidebar navItems={navItems} user={user} initials={initials} adminScopes={adminScopes} onLogout={handleLogout} onNavigate={() => setMobileNavOpen(false)} mobile />
          </SheetContent>
        </Sheet>
      </div>

      <main className="min-h-screen lg:ml-60">
        <Outlet />
      </main>
    </div>
  );
}

function AdminSidebar({ navItems, user, initials, adminScopes, onLogout, onNavigate, mobile = false }) {
  return (
    <>
      <div className="border-b border-white/10 px-5 pt-6 pb-5">
        <div className="mb-1 flex items-center gap-2.5">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-white text-[#0F0F12]">
            <Star className="w-4 h-4 fill-[#0F0F12]" strokeWidth={0} />
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight">Polaris Admin</div>
            <div className="text-caption uppercase tracking-widest text-white/40">Control Panel</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 space-y-0.5 px-3 py-4">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-white/10 text-white" : "text-white/50 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <item.icon className="w-4 h-4 flex-shrink-0" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className={`space-y-1 border-t border-white/10 px-3 pt-4 pb-5 ${mobile ? "mobile-safe-bottom" : ""}`}>
        <NavLink
          to="/app"
          onClick={onNavigate}
          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/50 transition-colors hover:bg-white/5 hover:text-white"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to App
        </NavLink>

        <div className="mt-1 flex items-center gap-3 px-3 py-2.5">
          <div className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full bg-white/10 text-xs font-semibold">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium">{user?.full_name}</div>
            <div className="truncate text-caption text-white/40">
              {user?.email}
              {!user?.is_superadmin && adminScopes.includes("blog") ? " · Blog manager" : ""}
            </div>
          </div>
          <button onClick={onLogout} className="text-white/30 transition-colors hover:text-white/70" title="Logout">
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </>
  );
}
